import { lazy } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import DashboardLayout from '@/components/layouts/DashboardLayout';
import PublicLayout from '@/components/layouts/PublicLayout';
import RootLayout from '@/components/layouts/RootLayout';
import AdminRoute from '@/components/shared/AdminRoute';
import ErrorBoundary from '@/components/shared/ErrorBoundary';
import NotFoundPage from '@/components/shared/NotFoundPage';
import { PermissionRoute } from '@/components/shared/PermissionRoute';
import ProtectedRoute from '@/components/shared/ProtectedRoute';
import PublicOnlyRoute from '@/components/shared/PublicOnlyRoute';
import { FEATURE_CHECKS } from '@/features/dms/constants/permissions';
import { getPublicRoutes, getSharedAdminRoutes, getSharedProtectedRoutes } from './shared';
import { withSuspense } from './utils';

// DMS-specific lazy-loaded pages
const FolderBrowserPage = lazy(() => import('@/features/dms/folders/pages/FolderBrowserPage'));
const DocumentDetailPage = lazy(() => import('@/features/dms/documents/pages/DocumentDetailPage'));
const ReviewsPage = lazy(() => import('@/features/dms/reviews/pages/ReviewsPage'));
const SearchResultsPage = lazy(() => import('@/features/dms/search/pages/SearchResultsPage'));
const SuppliersPage = lazy(() => import('@/features/dms/suppliers/pages/SuppliersPage'));
const SupplierDetailPage = lazy(() => import('@/features/dms/suppliers/pages/SupplierDetailPage'));
const CreatePurchaseOrderPage = lazy(
  () => import('@/features/dms/purchase-orders/pages/CreatePurchaseOrderPage')
);
const AdminDocumentTypesPage = lazy(
  () => import('@/features/dms/admin-document-types/pages/AdminDocumentTypesPage')
);
const AdminPdfStatusPage = lazy(
  () => import('@/features/dms/admin-pdf-status/pages/AdminPdfStatusPage')
);
const PurchasingSettingsPage = lazy(
  () => import('@/features/dms/purchasing-settings/pages/PurchasingSettingsPage')
);
const AteRunsPage = lazy(() => import('@/features/dms/ate/pages/AteRunsPage'));
const AteRunDetailPage = lazy(() => import('@/features/dms/ate/pages/AteRunDetailPage'));
const AteFailureAnalysisPage = lazy(
  () => import('@/features/dms/ate/pages/AteFailureAnalysisPage')
);
const AteFailureDetailsPage = lazy(() => import('@/features/dms/ate/pages/AteFailureDetailsPage'));
const AtePerformanceAnalysisPage = lazy(
  () => import('@/features/dms/ate/pages/AtePerformanceAnalysisPage')
);
const AteTokensPage = lazy(() => import('@/features/dms/ate/pages/AteTokensPage'));
const CapasPage = lazy(() => import('@/features/dms/capas/pages/CapasPage'));
const CapaDetailPage = lazy(() => import('@/features/dms/capas/pages/CapaDetailPage'));
const CreateCapaPage = lazy(() => import('@/features/dms/capas/pages/CreateCapaPage'));
const CapaSettingsPage = lazy(() => import('@/features/dms/capas/pages/CapaSettingsPage'));
const EcrsPage = lazy(() => import('@/features/dms/ecrs/pages/EcrsPage'));
const EcrDetailPage = lazy(() => import('@/features/dms/ecrs/pages/EcrDetailPage'));
const CreateEcrPage = lazy(() => import('@/features/dms/ecrs/pages/CreateEcrPage'));
const CalibrationPage = lazy(() => import('@/features/dms/calibration/pages/CalibrationPage'));
const CreateInstrumentPage = lazy(
  () => import('@/features/dms/calibration/pages/CreateInstrumentPage')
);
const InstrumentDetailPage = lazy(
  () => import('@/features/dms/calibration/pages/InstrumentDetailPage')
);

export function getDmsRoutes(): RouteObject[] {
  return [
    {
      element: (
        <ErrorBoundary>
          <RootLayout />
        </ErrorBoundary>
      ),
      errorElement: <NotFoundPage />,
      children: [
        // Public Routes
        {
          element: <PublicOnlyRoute />,
          children: [
            {
              element: <PublicLayout />,
              children: getPublicRoutes(),
            },
          ],
        },
        // Protected Routes
        {
          element: <ProtectedRoute />,
          children: [
            {
              element: <DashboardLayout />,
              children: [
                // DMS Routes
                { path: '/', element: withSuspense(FolderBrowserPage) },
                { path: '/folders/:folderId', element: withSuspense(FolderBrowserPage) },
                { path: '/documents/:documentNumber', element: withSuspense(DocumentDetailPage) },
                { path: '/reviews', element: withSuspense(ReviewsPage) },
                { path: '/search', element: withSuspense(SearchResultsPage) },
                // Purchasing Routes (feature-gated)
                {
                  path: '/suppliers',
                  element: (
                    <PermissionRoute permission={FEATURE_CHECKS.PURCHASING} redirectToLogin>
                      {withSuspense(SuppliersPage)}
                    </PermissionRoute>
                  ),
                },
                {
                  path: '/suppliers/:supplierId',
                  element: (
                    <PermissionRoute permission={FEATURE_CHECKS.PURCHASING} redirectToLogin>
                      {withSuspense(SupplierDetailPage)}
                    </PermissionRoute>
                  ),
                },
                {
                  path: '/purchase-orders/create',
                  element: (
                    <PermissionRoute permission={FEATURE_CHECKS.PURCHASING} redirectToLogin>
                      {withSuspense(CreatePurchaseOrderPage)}
                    </PermissionRoute>
                  ),
                },
                // CAPA Routes (feature-gated)
                {
                  path: '/capas',
                  element: (
                    <PermissionRoute permission={FEATURE_CHECKS.CAPAS} redirectToLogin>
                      {withSuspense(CapasPage)}
                    </PermissionRoute>
                  ),
                },
                {
                  path: '/capas/new',
                  element: (
                    <PermissionRoute permission={FEATURE_CHECKS.CAPAS} redirectToLogin>
                      {withSuspense(CreateCapaPage)}
                    </PermissionRoute>
                  ),
                },
                {
                  path: '/capas/:carNumber',
                  element: (
                    <PermissionRoute permission={FEATURE_CHECKS.CAPAS} redirectToLogin>
                      {withSuspense(CapaDetailPage)}
                    </PermissionRoute>
                  ),
                },
                // ECR Routes (feature-gated)
                {
                  path: '/ecrs',
                  element: (
                    <PermissionRoute permission={FEATURE_CHECKS.ECRS} redirectToLogin>
                      {withSuspense(EcrsPage)}
                    </PermissionRoute>
                  ),
                },
                {
                  path: '/ecrs/new',
                  element: (
                    <PermissionRoute permission={FEATURE_CHECKS.ECRS} redirectToLogin>
                      {withSuspense(CreateEcrPage)}
                    </PermissionRoute>
                  ),
                },
                {
                  path: '/ecrs/:ecrNumber',
                  element: (
                    <PermissionRoute permission={FEATURE_CHECKS.ECRS} redirectToLogin>
                      {withSuspense(EcrDetailPage)}
                    </PermissionRoute>
                  ),
                },
                // Calibration Routes (feature-gated)
                {
                  path: '/calibration',
                  element: (
                    <PermissionRoute permission={FEATURE_CHECKS.CALIBRATION} redirectToLogin>
                      {withSuspense(CalibrationPage)}
                    </PermissionRoute>
                  ),
                },
                {
                  path: '/calibration/new',
                  element: (
                    <PermissionRoute permission={FEATURE_CHECKS.CALIBRATION} redirectToLogin>
                      {withSuspense(CreateInstrumentPage)}
                    </PermissionRoute>
                  ),
                },
                {
                  path: '/calibration/:id',
                  element: (
                    <PermissionRoute permission={FEATURE_CHECKS.CALIBRATION} redirectToLogin>
                      {withSuspense(InstrumentDetailPage)}
                    </PermissionRoute>
                  ),
                },
                // Testing Routes (feature-gated)
                { path: '/testing/dashboard', element: <Navigate to="/testing/runs" replace /> },
                {
                  path: '/testing/runs',
                  element: (
                    <PermissionRoute permission={FEATURE_CHECKS.TESTING} redirectToLogin>
                      {withSuspense(AteRunsPage)}
                    </PermissionRoute>
                  ),
                },
                {
                  path: '/testing/runs/:id',
                  element: (
                    <PermissionRoute permission={FEATURE_CHECKS.TESTING} redirectToLogin>
                      {withSuspense(AteRunDetailPage)}
                    </PermissionRoute>
                  ),
                },
                {
                  path: '/testing/failure-analysis',
                  element: (
                    <PermissionRoute permission={FEATURE_CHECKS.TESTING} redirectToLogin>
                      {withSuspense(AteFailureAnalysisPage)}
                    </PermissionRoute>
                  ),
                },
                {
                  path: '/testing/failure-details/:criterion',
                  element: (
                    <PermissionRoute permission={FEATURE_CHECKS.TESTING} redirectToLogin>
                      {withSuspense(AteFailureDetailsPage)}
                    </PermissionRoute>
                  ),
                },
                {
                  path: '/testing/performance-analysis',
                  element: (
                    <PermissionRoute permission={FEATURE_CHECKS.TESTING} redirectToLogin>
                      {withSuspense(AtePerformanceAnalysisPage)}
                    </PermissionRoute>
                  ),
                },
                // Shared protected routes
                ...getSharedProtectedRoutes(),
                // Admin Routes (core - admin only)
                {
                  element: <AdminRoute />,
                  children: getSharedAdminRoutes(),
                },
                // Feature-gated admin routes
                {
                  path: '/admin/document-types',
                  element: (
                    <PermissionRoute permission={FEATURE_CHECKS.DOCUMENT_TYPES} redirectToLogin>
                      {withSuspense(AdminDocumentTypesPage)}
                    </PermissionRoute>
                  ),
                },
                {
                  path: '/admin/pdf-generation',
                  element: (
                    <PermissionRoute permission={FEATURE_CHECKS.PDF_GENERATION} redirectToLogin>
                      {withSuspense(AdminPdfStatusPage)}
                    </PermissionRoute>
                  ),
                },
                {
                  path: '/admin/purchasing-settings',
                  element: (
                    <PermissionRoute
                      permission={FEATURE_CHECKS.PURCHASING_SETTINGS}
                      redirectToLogin
                    >
                      {withSuspense(PurchasingSettingsPage)}
                    </PermissionRoute>
                  ),
                },
                {
                  path: '/admin/capa-settings',
                  element: (
                    <PermissionRoute permission={FEATURE_CHECKS.CAPA_SETTINGS} redirectToLogin>
                      {withSuspense(CapaSettingsPage)}
                    </PermissionRoute>
                  ),
                },
                {
                  path: '/admin/ate/tokens',
                  element: (
                    <PermissionRoute permission={FEATURE_CHECKS.ATE_TOKENS} redirectToLogin>
                      {withSuspense(AteTokensPage)}
                    </PermissionRoute>
                  ),
                },
              ],
            },
          ],
        },
      ],
    },
  ];
}
